import { useMemo, useState } from 'react';
import type { CallListClient, CallListBand, ClientStatusDetails } from '../../api/types';
import { InfoTip } from '../common/InfoTip';
import { ClientStatusControl } from './ClientStatusControl';
import { SalesforceLink } from '../shared/SalesforceLink';
import { REASON_CODE_LABEL } from './clientStatusVocab';
import './InspectionCallList.css';

const BAND_TIP: Record<CallListBand, string> = {
  new_client_no_repeat:
    'One inspection on record, 30–120 days ago, and no second visit. There is no interval to measure yet, so cadence alone would never surface these.',
  due_now:
    'This client is at or just past their own typical gap between inspections.',
  coming_up:
    'Approaching their typical gap — call before they are due, not after.',
  has_inventory_no_recent_inspection:
    'Holding 3 or more potential-for-inspection units — unlisted, in review/incomplete, or at a non-Manheim auction — with no inspection recently.',
  cadence_slipped:
    'Was inspecting on a regular rhythm and has now gone well past it.',
  dormant:
    'More than 3x their typical gap with no inspection. A win-back conversation rather than a scheduling call.',
};

const CONFIDENCE_TIP: Record<string, string> = {
  high: 'How dependable this client\u2019s due date is. High = 6+ gaps between visits and a tight spread.',
  medium: 'How dependable this client\u2019s due date is. Medium = at least 3 gaps, with some variability.',
  low: 'How dependable this client\u2019s due date is. Low = few gaps or a wide spread \u2014 treat the due date as a rough guide.',
  none: 'No repeat visits yet, so there is no interval to estimate a due date from.',
};

interface Props {
  clients: CallListClient[];
  loading: boolean;
  bandCounts: Partial<Record<CallListBand, number>>;
  suppressedCount: number;
  hiddenCount: number;
  statusOptions: string[];
  reasonCodes: string[];
  permanentStatuses: string[];
  selectedClientId: string | null;
  includeSuppressed: boolean;
  includeDormant: boolean;
  includeHidden: boolean;
  sortBy: 'due' | 'distance';
  isLocationMode: boolean;
  onToggleSuppressed: (value: boolean) => void;
  onToggleDormant: (value: boolean) => void;
  onToggleHidden: (value: boolean) => void;
  onSortByChange: (value: 'due' | 'distance') => void;
  onClientClick: (sellerId: string) => void;
  onSetStatus: (sellerId: string, status: string, details: ClientStatusDetails) => void;
  onClearStatus: (sellerId: string) => void;
}

function cadenceDetail(client: CallListClient): string {  const c = client.cadence;
  if (c.effective_inspection_interval_days == null) {
    return 'No repeat inspections yet, so there is no interval to measure.';
  }
  const gaps = c.effective_interval_gap_count ?? 0;
  const win = c.effective_interval_window_days ?? 0;
  const iqr = c.inspection_interval_iqr_days;
  const spread = iqr == null ? '' : ` Spread ±${Math.round(iqr)} days.`;
  const trend = c.inspection_cadence_trend
    ? ` Trend: ${c.inspection_cadence_trend.replace('_', ' ')}.`
    : '';
  return `Median of ${gaps} gap${gaps === 1 ? '' : 's'} between inspection days over the last ${win} days.${spread}${trend}`;
}

/**
 * Status control. The menu is positioned `fixed` off the button's measured rect
 * because the list scrolls — an absolutely-positioned menu is clipped by the
 * scroll container and never appears.
 */
function ContactLine({ client }: { client: CallListClient }) {
  const c = client.contact;
  if (c.days_since_last_contact == null) {
    return <span className="cl-contact-none">No logged contact</span>;
  }
  // No direction arrow: last_contact_direction is logged inconsistently and "inbound" is usually
  // us calling them, so showing it told reps the opposite of what happened. The agent who logged
  // it and the subject are the reliable evidence.
  const detail = [c.last_contact_type, c.last_contact_subject].filter(Boolean).join(' · ');
  return (
    <span>
      Contacted {c.days_since_last_contact}d ago
      {c.last_contact_agent && ` by ${c.last_contact_agent}`}
      {detail && <InfoTip content={detail} />}
    </span>
  );
}

export function InspectionCallList({
  clients,
  loading,
  bandCounts,
  suppressedCount,
  hiddenCount,
  statusOptions,
  reasonCodes,
  permanentStatuses,
  selectedClientId,
  includeSuppressed,
  includeDormant,
  includeHidden,
  sortBy,
  isLocationMode,
  onToggleSuppressed,
  onToggleDormant,
  onToggleHidden,
  onSortByChange,
  onClientClick,
  onSetStatus,
  onClearStatus,
}: Props) {
  // Sections start closed so the rep sees the shape of the work first and opens
  // the one they intend to work, rather than scrolling a single long list.
  const [expanded, setExpanded] = useState<Set<CallListBand>>(new Set());

  // Rows arrive pre-sorted by band; group without re-ordering so the backend
  // stays the single source of truth for priority.
  const sections = useMemo(() => {
    const out: { band: CallListBand; label: string; rows: CallListClient[] }[] = [];
    for (const client of clients) {
      const last = out[out.length - 1];
      if (last && last.band === client.band) last.rows.push(client);
      else out.push({ band: client.band, label: client.band_label, rows: [client] });
    }
    return out;
  }, [clients]);

  const toggleSection = (band: CallListBand) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(band)) next.delete(band);
      else next.add(band);
      return next;
    });
  };

  return (
    <div className="cl-panel">
      <div className="cl-toolbar">
        <div className="cl-toolbar-line">
          <span className="cl-count">
            {loading ? 'Loading…' : `${clients.length.toLocaleString()} to call`}
          </span>
          <div className="cl-toolbar-right">
            <button
              type="button"
              className="cl-link-btn"
              onClick={() => setExpanded(
                expanded.size === sections.length
                  ? new Set()
                  : new Set(sections.map((s) => s.band)),
              )}
              disabled={sections.length === 0}
            >
              {expanded.size === sections.length && sections.length > 0 ? 'Collapse all' : 'Expand all'}
            </button>
            <label className="cl-sort">
              Sort
              <select value={sortBy} onChange={(e) => onSortByChange(e.target.value as 'due' | 'distance')}>
                <option value="due">Most due</option>
                <option value="distance" disabled={!isLocationMode}>Closest</option>
              </select>
              <InfoTip content="'Most due' ranks each client against their own inspection rhythm, so low-volume accounts are not buried by high-volume ones. 'Closest' needs a location set above." />
            </label>
          </div>
        </div>
        <div className="cl-toolbar-line cl-toolbar-toggles">
          <label>
            <input type="checkbox" checked={includeDormant} onChange={(e) => onToggleDormant(e.target.checked)} />
            Show dormant
            <InfoTip content="Clients more than 3x past their typical gap. Hidden by default — they are a win-back conversation, not today's scheduling call." />
          </label>
          <label>
            <input type="checkbox" checked={includeSuppressed} onChange={(e) => onToggleSuppressed(e.target.checked)} />
            Show worked ({suppressedCount})
            <InfoTip content="Clients held back by an active status you set, or because Salesforce shows we contacted them very recently." />
          </label>
          <label>
            <input type="checkbox" checked={includeHidden} onChange={(e) => onToggleHidden(e.target.checked)} />
            Show hidden ({hiddenCount})
            <InfoTip content="Clients taken off the list for good — Inlane Only, or Milk Run where cases are created automatically. Unlike the other statuses these never come back on their own; tick this to find one and clear its status." />
          </label>
        </div>
      </div>

      <div className="cl-list">
        {loading && clients.length === 0 ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="cl-row-skeleton" aria-hidden="true" />
          ))
        ) : clients.length === 0 ? (
          <div className="cl-empty">
            No clients need a scheduling call in this territory right now.
          </div>
        ) : (
          sections.map((section) => {
            const isOpen = expanded.has(section.band);
            return (
              <section key={section.band} className={`cl-section cl-section-${section.band}`}>
                <button
                  type="button"
                  className="cl-section-header"
                  aria-expanded={isOpen}
                  onClick={() => toggleSection(section.band)}
                >
                  <span className={`cl-chevron${isOpen ? ' cl-chevron-open' : ''}`} aria-hidden="true">▶</span>
                  <span className="cl-section-title">{section.label}</span>
                  <span className="cl-section-count">{bandCounts[section.band] ?? section.rows.length}</span>
                  <InfoTip content={BAND_TIP[section.band]} />
                </button>

                {isOpen && (
                  <div className="cl-section-body">
                    {section.rows.map((client) => {
                      const conf = client.cadence.inspection_cadence_confidence || 'none';
                      const status = client.status;
                      const isSelected = client.seller_id === selectedClientId;
                      return (
                        <div
                          key={client.seller_id}
                          className={`cl-row${isSelected ? ' cl-row-selected' : ''}`}
                          onClick={() => onClientClick(client.seller_id)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => { if (e.key === 'Enter') onClientClick(client.seller_id); }}
                        >
                          <div className="cl-row-main">
                            <div className="cl-name">{client.seller_name}</div>

                            <div className="cl-cadence">
                              {client.cadence_summary}
                              <InfoTip content={cadenceDetail(client)} />
                              <span className={`cl-conf cl-conf-${conf}`}>
                                {conf} confidence
                                <InfoTip content={CONFIDENCE_TIP[conf] ?? ''} />
                              </span>
                            </div>

                            <div className="cl-row-meta">
                              <span>
                                {[client.territory_city, client.territory_state].filter(Boolean).join(', ') || client.zip_code}
                              </span>
                              {client.distance_miles != null && <span>{client.distance_miles} mi</span>}
                              {client.pfi_vin_count > 0 && (
                                <span className="cl-pfi">
                                  {client.pfi_vin_count} potential inspection{client.pfi_vin_count === 1 ? '' : 's'}
                                  <InfoTip content="Units this client holds that are unlisted, in review/incomplete, or at a non-Manheim auction — the 'Potential for Inspection' bucket on the priority chips." />
                                </span>
                              )}
                              <ContactLine client={client} />
                            </div>
                          </div>

                          <div className="cl-row-side" onClick={(e) => e.stopPropagation()}>
                            {status?.status && (
                              <span className={`cl-status-chip${status.is_expired ? ' cl-status-chip-stale' : ''}`}>
                                {status.age_days != null ? `${status.age_days}d ago` : 'set'}
                                <InfoTip
                                  content={
                                    status.is_expired
                                      ? status.expiry_reason === 'inspected_since'
                                        ? 'This client has been inspected since the status was set — the outreach worked.'
                                        : status.expiry_reason === 'contacted_since'
                                          ? 'Salesforce shows a contact after this status was set, so it may be out of date.'
                                          : 'Past its useful life — worth another look.'
                                      : `Set by ${status.user_email ?? 'a rep'}.`
                                  }
                                />
                              </span>
                            )}
                            {(status?.reason_code || status?.reason_text) && (
                              <span className="cl-status-reason">
                                {status.reason_code
                                  ? (REASON_CODE_LABEL[status.reason_code] ?? status.reason_code)
                                  : 'Note'}
                                <InfoTip content={status.reason_text || 'No note was left.'} />
                              </span>
                            )}
                            <ClientStatusControl
                              value={status}
                              options={statusOptions}
                              reasonCodes={reasonCodes}
                              permanentStatuses={permanentStatuses}
                              onSet={(s, details) => onSetStatus(client.seller_id, s, details)}
                              onClear={() => onClearStatus(client.seller_id)}
                            />
                            <SalesforceLink salesforceId={client.salesforce_id} className="cl-sf-link" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            );
          })
        )}
      </div>
    </div>
  );
}

export default InspectionCallList;
